import Messenger from '../tnew/ui/Messenger';
import { FormErrorMessageRenderer } from './FormErrorMessageRenderer';
import type FormHandler from './FormHandler';
import type { ControlGroupResultPair, ProcessValidationResultsOptions } from './types';
import { ValidationResult } from './ValidationService';

export default class FormErrorMessenger {
  messenger: Messenger;

  private readonly errorHeadingId = 'tn-form-errors-alert-heading';

  private readonly errorMessageRenderer;

  constructor(private formHandler: FormHandler, $el: Element, private headingLevel: number = 2) {
    if (headingLevel < 1 || headingLevel > 6) {
      throw new Error(`FormErrorMessenger headingLevel parameter is ${headingLevel}, should be between 0 and 7`);
    }

    $el.setAttribute('tabindex', '-1');

    this.messenger = new Messenger($el);
    this.errorMessageRenderer = new FormErrorMessageRenderer(this.headingLevel, this.errorHeadingId);
  }

  clear() {
    this.messenger.clearMessages();
  }

  focusOnFirstErrorAnchor() {
    const $anchor = this.messenger.$messageContainer.querySelector<HTMLAnchorElement>('ul li:first-child a');
    if ($anchor) $anchor.focus();
  }

  processValidationResults(validationResults: ValidationResult[], options: ProcessValidationResultsOptions = {}) {
    const sortedPairs = validationResults
      .filter((validation) => this.filterValidationResults(validation, options))
      .map((validation) => this.buildControlGroupPair(validation))
      .sort((a, b) => this.sortControlGroupResultPairs(a, b));

    const html = this.errorMessageRenderer.renderErrorMessage(sortedPairs);

    if (html) {
      this.messenger.addErrorMessage(html, false);
      this.focusOnFirstErrorAnchor();
    }
  }

  private filterValidationResults(validationResult: ValidationResult, options: ProcessValidationResultsOptions) {
    return options.includeNonFormErrors || this.formHandler.controlGroups.has(validationResult.fieldId);
  }

  private processControlGroupPair(pair: ControlGroupResultPair) {
    const { controlGroup, validationResult } = pair;

    if (controlGroup) {
      controlGroup.clearErrors();
      const showError = !validationResult.isValid && controlGroup.isVisible() && validationResult.message;
      if (showError) controlGroup.showError(validationResult.message);
    }

    return pair;
  }

  private buildControlGroupPair(validation: ValidationResult): ControlGroupResultPair {
    const controlGroup = this.formHandler.getControlGroup(validation.fieldId);
    const pair = { controlGroup, validationResult: validation };
    return this.processControlGroupPair(pair);
  }

  private sortControlGroupResultPairs(a: ControlGroupResultPair, b: ControlGroupResultPair) {
    if (a.controlGroup === undefined && b.controlGroup !== undefined) return 1;
    if (a.controlGroup !== undefined && b.controlGroup === undefined) return -1;

    if (a.controlGroup && b.controlGroup) {
      if (a.controlGroup?.order < b.controlGroup?.order) return -1;
      if (a.controlGroup?.order > b.controlGroup?.order) return 1;
    }

    return 0;
  }
}
